import { listVideoAssetsByCanvasNode, batchDeleteVideoAssets } from '../../../api'
import type { Node } from '@xyflow/react'

export type CanvasNodeVideoAssetRef = {
  id: string
  job_id: string
  title: string
}

export type VideoNodeDeletePlan = {
  nodeIds: string[]
  assetIds: string[]
  assetCount: number
}

export function isVideoGeneratorNode(node: Node | undefined | null): boolean {
  if (!node) return false
  const data = node.data as { nodeType?: string } | undefined
  return node.type === 'video_generator' || data?.nodeType === 'generate_video'
}

export function nodeMayHavePersistedVideoAsset(node: Node): boolean {
  if (!isVideoGeneratorNode(node)) return false
  const fields = ((node.data as { fields?: Record<string, unknown> })?.fields || {}) as Record<string, unknown>
  return Boolean(fields.generatedTaskId || fields.generatedVideoUrl)
}

export async function resolveVideoDeletePlan(
  workspaceId: string,
  nodes: Node[],
  nodeIds: string[],
): Promise<VideoNodeDeletePlan | null> {
  const videoNodeIds = nodeIds.filter((id) => {
    const node = nodes.find((n) => n.id === id)
    return node && isVideoGeneratorNode(node)
  })
  if (videoNodeIds.length === 0) return null

  const assetIdSet = new Set<string>()
  await Promise.all(
    videoNodeIds.map(async (nodeId) => {
      try {
        const { data } = await listVideoAssetsByCanvasNode(workspaceId, nodeId)
        for (const item of data.items) assetIdSet.add(item.id)
      } catch {
        const node = nodes.find((n) => n.id === nodeId)
        if (node && nodeMayHavePersistedVideoAsset(node)) {
          const fields = ((node.data as { fields?: Record<string, unknown> })?.fields || {}) as Record<string, unknown>
          const taskId = fields.generatedTaskId
          if (typeof taskId === 'string' && taskId) assetIdSet.add(`job:${taskId}`)
        }
      }
    }),
  )

  if (assetIdSet.size === 0) return null
  return {
    nodeIds,
    assetIds: [...assetIdSet],
    assetCount: assetIdSet.size,
  }
}

export async function deleteVideoAssetsForPlan(workspaceId: string, plan: VideoNodeDeletePlan): Promise<void> {
  if (plan.assetIds.length === 0) return
  const { data } = await batchDeleteVideoAssets(workspaceId, plan.assetIds)
  if (data.failed_count > 0 && data.deleted_count === 0) {
    const first = data.failed[0]
    throw new Error(first?.message || '视频资产删除失败')
  }
}
